import VueMultiselectExtended from './inputs/multiselect';
import {Field, defineRule} from 'vee-validate';
import {regex} from '@vee-validate/rules';
import utils from '../shared/utils';

defineRule('regex', regex);

function initMortgageCalculator(app) {
    import('moment').then(({default: moment}) => {
        app.component('mortgageCalculator',
            {
                template: '#mortgage-calculator',
                props: {
                    propertyPrice: {},
                    defaultInterestRate: {},
                    labels: {},
                    currency: {},
                    paymentFrequencies: {},
                },
                components: {
                    Multiselect: VueMultiselectExtended,
                    ValidationField: Field,
                },
                data() {
                    return {
                        loanTerm: 30,
                        deposit: null,
                        interestRate: this.defaultInterestRate || 5,
                        defaultPaymentFrequency: 30,
                        selectedFrequency: null,
                        loanValue: null,
                        localStorageId: 'mortgage-calculator',
                        termsList: [],
                        maxTermInYears: 30,
                        currencySign: this.currency || '$',
                        debounceTime: 500,
                        modalOpened: false,
                    };
                },
                methods: {
                    initCalc() {
                        this.termsList = Array.from({length: this.maxTermInYears}, (v, k) => k + 1);
                        this.loanValue = Math.round(this.toNumber(this.propertyPrice));
                        this.setPaymentFrequency(this.defaultPaymentFrequency);
                    },

                    setPaymentFrequency(frequency) {
                        if (!frequency || frequency <= 0) {
                            frequency = this.defaultPaymentFrequency;
                        }

                        this.selectedFrequency = this.paymentFrequencies.find((f) => f.term == frequency);
                    },

                    toNumber(value) {
                        return value != null ? utils.parseNumber(value) : null;
                    },

                    getYearLabel(year) {
                        return moment.duration(year, 'years').humanize();
                    },

                    loadSavedCalculatorSettings() {
                        try {
                            const itemString = localStorage.getItem(this.localStorageId);

                            if (itemString) {
                                Object.assign(this, JSON.parse(itemString));
                                this.setPaymentFrequency(this.savedPaymentFrequency);
                            }
                        } catch (ex) {

                        }
                    },

                    saveCalculatorSettings() {
                        const settings = {
                            loanTerm: this.loanTerm,
                            deposit: this.deposit,
                            interestRate: this.interestRate,
                            savedPaymentFrequency: this.selectedFrequency.term,
                        };

                        localStorage.setItem(this.localStorageId, JSON.stringify(settings));
                    },

                    getTermsCountPerYear() {
                        const paymentFrequency = this.selectedFrequency.term;

                        if (paymentFrequency === this.defaultPaymentFrequency) {
                            return 12;
                        }

                        return 365.25 / paymentFrequency;
                    },
                    containsNumber(str) {
                        const globalizedDecimalWithSpaces = new RegExp(/^(\b\d+([,\s.]\d+)*\b)$/);
                        return globalizedDecimalWithSpaces.test(str);
                    },
                },
                computed: {
                    validateInputs() {
                        const isValidDeposit = this.containsNumber(this.deposit) && this.toNumber(this.deposit) > 0;
                        const isValidLoanValue = this.containsNumber(this.loanValue) && this.toNumber(this.loanValue) > 0;
                        const isValidDepositLoanValueDependency = (this.deposit == null || this.deposit === '' || this.toNumber(this.loanValue) > this.toNumber(this.deposit) );

                        return this.interestRate &&
                            this.interestRate > 0 &&
                            this.selectedFrequency &&
                            this.selectedFrequency.term &&
                            isValidLoanValue &&
                            isValidDeposit &&
                            isValidDepositLoanValueDependency;
                    },

                    termPayments() {
                        if (this.validateInputs) {
                            const termsCountPerYear = this.getTermsCountPerYear();
                            const termInterestRate = this.interestRate / (termsCountPerYear * 100);
                            const termsCount = this.loanTerm * termsCountPerYear;
                            const x = 1 - Math.pow(1 + termInterestRate, -termsCount);
                            const sum = utils.parseNumber(this.loanValue) - (utils.parseNumber(this.deposit) || 0);

                            return sum * termInterestRate / x;
                        }

                        return null;
                    },
                    totalCost() {
                        if (this.validateInputs && this.termPayments) {
                            const termsCountPerYear = this.getTermsCountPerYear();

                            if (termsCountPerYear) {
                                this.saveCalculatorSettings();
                                return termsCountPerYear * this.loanTerm * this.termPayments;
                            }
                        }

                        return null;
                    },
                    totalInterest() {
                        if (this.validateInputs && this.totalCost) {
                            const sum = utils.parseNumber(this.loanValue) - (utils.parseNumber(this.deposit) || 0);
                            return this.totalCost - sum;
                        }

                        return null;
                    },
                },
                mounted() {
                    this.initCalc();
                    this.loadSavedCalculatorSettings();
                },
            });
    });
}

export default initMortgageCalculator;
